/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jul 24 2024
 **/

import styled from "@emotion/styled";

import { Choice } from "@/frameworks/libraries/components";
import { Icon, Row } from "@/frameworks/designStuff/components";

import { Avatar } from "../Avatar";
import * as S from "./private/emotion";
import { colors } from "../../styles";

type CallType = "calling" | "before_call" | "no_response" | "none";

type CoreCallAvatarProps = {
	id: string;
	size: "large" | "small";
	useStatus?: boolean;
	showMiniProfile?: boolean;
	mySelf?: boolean;
	micStatus?: MediaState;
	callType?: CallType;
	isSpeaking?: boolean;
};

const BORDER_OFFSET = 9;

export default function CallAvatar({ callType = "none", micStatus, isSpeaking, ...rest }: CoreCallAvatarProps) {
	const kind = rest.size === "large" ? "people_80" : "people_40";
	const defaultSize = kind === "people_80" ? 88 : 40;
	return (
		<AvatarBackground callType={callType} defaultSize={defaultSize} isSpeaking={isSpeaking}>
			<Choice value={callType}>
				<Choice.Case test={"before_call"}>
					<S.CloseButtonWrapper verticalAlign="center" horizonAlign="center" kind={rest.size}>
						<Icon name="Close24" size={14} fill="white" />
					</S.CloseButtonWrapper>
				</Choice.Case>
				<Choice.Case test={"calling"}>
					<Row
						style={{
							width: `${defaultSize}px`,
							height: `${defaultSize}px`,
							backgroundColor: "rgba(0, 132, 74, 0.4)",
							position: "absolute",
							borderRadius: "50%",
							zIndex: 3
						}}
					>
						<S.CallIconWrapper
							verticalAlign="center"
							horizonAlign="center"
							kind={rest.size}
							callType={callType}
						>
							<Icon name="Call0224" size={20} fill={colors.neutral_00_100} />
						</S.CallIconWrapper>
					</Row>
				</Choice.Case>
				<Choice.Case test={"no_response"}>
					<Row
						style={{
							width: `${defaultSize}px`,
							height: `${defaultSize}px`,
							backgroundColor: "rgba(200, 35, 53, 0.4)",
							position: "absolute",
							borderRadius: "50%",
							zIndex: 3
						}}
					>
						<S.CallIconWrapper
							verticalAlign="center"
							horizonAlign="center"
							kind={rest.size}
							callType={callType}
						>
							<Icon name="CallUnansweredTest24" size={30} fill={"white"} />
						</S.CallIconWrapper>
					</Row>
				</Choice.Case>
			</Choice>
			{micStatus === "on" && (
				<S.MicIconWrapper verticalAlign="center" horizonAlign="center" kind={rest.size} callType={callType}>
					<Icon name="MicOff24" size={14} fill="white" />
				</S.MicIconWrapper>
			)}
			<Avatar {...rest} kind={kind} />
		</AvatarBackground>
	);
}
interface AvatarType {
	isSpeaking?: boolean;
	callType: CallType;
	defaultSize: number;
}
const AvatarBackground = styled.div<AvatarType>`
	position: relative;
	display: flex;
	z-index: 5;
	align-items: center;
	justify-content: center;
	width: ${({ defaultSize }) => defaultSize + BORDER_OFFSET}px;
	height: ${({ defaultSize }) => defaultSize + BORDER_OFFSET}px;
	border-radius: 50%;
	${({ callType, theme }) =>
		callType === "before_call"
			? `border: 6px solid ${theme.colors.error_40_100}`
			: callType === "calling"
				? `border: 6px solid ${theme.colors.success_80_100}`
				: callType === "no_response"
					? `border: 6px solid ${theme.colors.error_60_100}`
					: ""}
	${({ isSpeaking, theme }) =>
		isSpeaking
			? `	border: 3px solid transparent;
					border: 6px solid transparent;
					background-image: linear-gradient(#fff,#fff),
					${theme.colors.gradient_40_100};
					background-origin: border-box;
					background-clip: padding-box, border-box;`
			: ""}
`;
